import { Mic } from 'lucide-react';
import React from 'react';
import { Button } from './ui/button';

interface ManualCaptureModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ManualCaptureModal: React.FC<ManualCaptureModalProps> = ({
  isOpen,
  onClose
}) => {
  if (!isOpen) return null;

  const downloadUrl = '/downloads/ApexiaLabsDesktop-Setup.exe'; // Placeholder URL

  return (
    <div className="fixed inset-0 z-[1300] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-[rgba(28,27,24,0.5)]"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative modal-panel w-full max-w-md mx-4 p-6 animate-fadeIn">
        {/* Close button */}
        <Button
          variant="tertiary"
          onClick={onClose}
          className="absolute top-4 right-4"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </Button>

        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-signature-tint rounded-full flex items-center justify-center mx-auto mb-4">
            <Mic className="h-8 w-8 text-signature" aria-hidden="true" />
          </div>
          <h2 className="heading-2">Manual Audio Capture</h2>
          <p className="text-ink-muted mt-2">
            Capture audio directly from your computer without using a meeting bot
          </p>
        </div>

        {/* Content */}
        <div className="space-y-4">
          {/* Requirements */}
          <div className="bg-info-tint border border-info rounded-lg p-4">
            <h3 className="font-semibold text-ink mb-2">Requirements</h3>
            <ul className="text-sm text-ink-secondary space-y-1">
              <li className="flex items-center gap-2">
                <span className="text-ink-secondary">✓</span>
                Windows 10 or later
              </li>
              <li className="flex items-center gap-2">
                <span className="text-ink-secondary">✓</span>
                Microphone for your voice
              </li>
              <li className="flex items-center gap-2">
                <span className="text-ink-secondary">✓</span>
                System audio for customer voice
              </li>
            </ul>
          </div>

          {/* How it works */}
          <div className="bg-surface-subtle rounded-lg p-4">
            <h3 className="font-semibold text-ink mb-2">How it works</h3>
            <ol className="text-sm text-ink-secondary space-y-2">
              <li className="flex items-start gap-2">
                <span className="bg-surface-sunken rounded-full w-5 h-5 flex items-center justify-center text-xs font-medium flex-shrink-0">1</span>
                <span>Download and install the desktop app</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-surface-sunken rounded-full w-5 h-5 flex items-center justify-center text-xs font-medium flex-shrink-0">2</span>
                <span>Sign in with your Google account</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-surface-sunken rounded-full w-5 h-5 flex items-center justify-center text-xs font-medium flex-shrink-0">3</span>
                <span>Click "Start Session" to begin capturing audio</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-surface-sunken rounded-full w-5 h-5 flex items-center justify-center text-xs font-medium flex-shrink-0">4</span>
                <span>View real-time analysis here on the dashboard</span>
              </li>
            </ol>
          </div>

          {/* Note about macOS */}
          <div className="bg-warning-tint border border-warning rounded-lg p-3">
            <p className="text-sm text-ink">
              <strong>Note:</strong> Currently Windows only. macOS support coming soon.
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-6 space-y-3">
          <a
            href={downloadUrl}
            className="block w-full bg-signature hover:bg-signature-strong text-on-signature text-center py-3 px-4 rounded-control font-medium transition-colors"
            download
          >
            Download Desktop App
          </a>
          <Button
            variant="secondary"
            onClick={onClose}
            className="w-full"
          >
            Cancel
          </Button>
        </div>

        {/* Footer */}
        <p className="text-xs text-center text-ink-muted mt-4">
          Already have the app? Just open it and sign in to start capturing.
        </p>
      </div>
    </div>
  );
};

export default ManualCaptureModal;
